'use client'

import {
  FeatureRow,
  FeatureWithTooltip,
  PaidPlanCard,
} from '@/components/pricing-v3/paid-plan-card'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'
import { ScrollIndicator } from './scroll-indicator'
import { Button } from '../ui/button'
import { Switch } from '../ui/switch'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { pushModal } from '../modals'
import { cn } from '@/lib/utils'

interface SelectOption {
  value: string
  label: string
  price: number
}

interface IPricingCardsProps {
  isAnnual: boolean
  basicCredits: string
  setBasicCredits: (value: string) => void
  premiumCredits: string
  setPremiumCredits: (value: string) => void
  platinumCredits: string
  setPlatinumCredits: (value: string) => void
  basicDomains: string
  setBasicDomains: (value: string) => void
  premiumDomains: string
  setPremiumDomains: (value: string) => void
  platinumDomains: string
  setPlatinumDomains: (value: string) => void
  loadingPlan: 'basic' | 'premium' | 'platinum' | null
  getBasicPrice: () => number
  getPremiumPrice: () => number
  getPlatinumPrice: () => number
  getButtonText: (plan: 'basic' | 'premium' | 'platinum') => string
  getSubscriptionAction: (
    targetPlan: 'basic' | 'premium' | 'platinum',
    targetCredits?: string,
    targetDomains?: string,
  ) => 'new' | 'current' | 'upgrade' | 'downgrade' | 'change'
  isButtonDisabled: (plan: 'basic' | 'premium' | 'platinum') => boolean
  handleSubscribe: (plan: 'basic' | 'premium' | 'platinum') => Promise<void>
  basicCreditOptions: SelectOption[]
  premiumCreditOptions: SelectOption[]
  platinumCreditOptions: SelectOption[]
  domainOptions: SelectOption[]
  showDomainSelect?: boolean
  minRequiredPlan?: 'basic' | 'premium' | 'platinum'
  currentPlan?: 'basic' | 'premium' | 'platinum' | 'free'
  subscriptionStatus?: string
  hasPaymentIssue?: boolean
  hasIncompleteSubscription?: boolean
  showEnterpriseCard?: boolean
  isModal?: boolean
  showDomainToggle?: boolean
  setShowDomainToggle?: (value: boolean) => void
  curDomains?: number
  hideFreeAndBasic?: boolean
}

/**
 * Pricing cards section displaying Free, Basic, Premium, and Platinum plans
 */
export function PricingCards({
  isAnnual,
  basicCredits,
  setBasicCredits,
  premiumCredits,
  setPremiumCredits,
  platinumCredits,
  setPlatinumCredits,
  basicDomains,
  setBasicDomains,
  premiumDomains,
  setPremiumDomains,
  platinumDomains,
  setPlatinumDomains,
  getBasicPrice,
  getPremiumPrice,
  getPlatinumPrice,
  getButtonText,
  getSubscriptionAction,
  isButtonDisabled,
  handleSubscribe,
  basicCreditOptions,
  premiumCreditOptions,
  platinumCreditOptions,
  domainOptions,
  showDomainSelect = false,
  minRequiredPlan,
  currentPlan,
  hasPaymentIssue,
  hasIncompleteSubscription,
  showEnterpriseCard = false,
  isModal = false,
  showDomainToggle,
  setShowDomainToggle,
  curDomains,
}: IPricingCardsProps) {
  const t = useTranslations('pricing-v3')
  const containerRef = useRef<HTMLDivElement>(null)

  // Show current plan badge
  // currentPlan will be 'free' if no subscription, but we still show it if user is logged in
  // We check if currentPlan is defined (not undefined) to determine if user is logged in
  const shouldShowCurrentPlan = currentPlan !== undefined

  // Determine which plan to highlight (not the current plan)
  // Normally highlight Premium ($49.99), but if user is on Premium, highlight Platinum instead
  const getHighlightedPlan = (): 'basic' | 'premium' | 'platinum' | null => {
    if (currentPlan === 'premium') return 'platinum'
    return 'premium' // Default: highlight Premium
  }

  const highlightedPlan = getHighlightedPlan()

  // Removed auto-scroll to Premium on mobile
  // Mobile users should see all paid plans from Basic -> Premium -> Platinum
  // to establish price anchoring and understand the value proposition

  // Determine which cards to show based on minRequiredPlan
  // Show all plans at or above the minimum required plan
  const showBasicCard = !minRequiredPlan || minRequiredPlan === 'basic'
  const showPremiumCard =
    !minRequiredPlan ||
    minRequiredPlan === 'basic' ||
    minRequiredPlan === 'premium'
  const showPlatinumCard = true // Always show platinum

  const visibleCardCount =
    (showBasicCard ? 1 : 0) +
    (showPremiumCard ? 1 : 0) +
    (showPlatinumCard ? 1 : 0) +
    (showEnterpriseCard ? 1 : 0)

  /**
   * Get tooltip content for disabled subscription button
   * Returns tooltip message if button should be disabled with tooltip, null otherwise
   * Priority: isUserPremium > hasProjectSubscription > hasIncompleteSubscription > hasPaymentIssue
   * Note: isMigratedSubscription removed - migrated users CAN upgrade their workspace subscriptions
   */
  const getDisabledTooltip = () => {
    if (hasIncompleteSubscription) {
      return (
        <>
          {t('incomplete_subscription_tooltip')}{' '}
          <a
            href="/subscription"
            style={{ color: '#FDD577', textDecoration: 'underline' }}
          >
            Go to Subscriptions
          </a>
        </>
      )
    }
    if (hasPaymentIssue) {
      return t('payment_issue_tooltip')
    }
    return null
  }

  // Mobile: vertical stack, Desktop: grid layout
  const gridClass =
    visibleCardCount === 1
      ? 'flex flex-col md:grid md:grid-cols-1 gap-6 md:gap-8 max-w-md mx-auto px-4 md:px-0'
      : visibleCardCount === 2
        ? 'flex flex-col md:grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto px-4 md:px-0'
        : visibleCardCount === 3
          ? 'flex flex-col md:grid md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto px-4 md:px-0'
          : visibleCardCount === 4
            ? 'flex flex-col md:grid md:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto px-4 md:px-0'
            : 'flex flex-col md:grid md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8 max-w-[100rem] mx-auto px-4 md:px-0'

  return (
    <section
      className={`pb-16 relative ${isModal ? '' : 'pricing-cards-section'}`}
    >
      {/* Mobile scroll hint */}
      <div className="md:hidden text-center text-white/60 text-sm pb-4 px-4">
        ↓ {t('scroll_to_view_all_plans') || '向下滚动查看所有方案'} ↓
      </div>

      <div
        ref={containerRef}
        className={`${gridClass} ${isModal ? '' : 'pricing-cards-container'}`}
      >
        {/* Basic Plan */}
        {showBasicCard && (
          <PaidPlanCard
            plan="basic"
            title={t('plans.basic.title')}
            tagline={t('plans.basic.tagline')}
            description={t('plans.basic.description')}
            icon="/icons/basic.svg"
            price={getBasicPrice()}
            isAnnual={isAnnual}
            perMonthText={t('billing.per_month')}
            billedAnnuallyText={(amount) => {
              return t('billing.billed_annually_total', { amount })
            }}
            credits={basicCredits}
            setCredits={setBasicCredits}
            creditOptions={basicCreditOptions}
            domains={basicDomains}
            setDomains={setBasicDomains}
            domainOptions={domainOptions}
            showDomainSelect={showDomainSelect}
            getButtonText={getButtonText}
            getSubscriptionAction={getSubscriptionAction}
            isButtonDisabled={isButtonDisabled}
            handleSubscribe={handleSubscribe}
            disabledTooltip={getDisabledTooltip()}
            isCurrentPlan={shouldShowCurrentPlan && currentPlan === 'basic'}
            shouldHighlight={highlightedPlan === 'basic'}
            features={
              <>
                <div>
                  <span>{t('plans.basic.features.everything_free_plus')}</span>
                </div>
                <FeatureRow>
                  <span>
                    {t('plans.basic.features.custom_domains', {
                      count: basicDomains,
                    })}
                  </span>
                </FeatureRow>
                <FeatureRow>
                  <FeatureWithTooltip
                    text={t('plans.basic.features.private_projects')}
                    tooltip={t('plans.basic.features.private_projects_tooltip')}
                  />
                </FeatureRow>
                <FeatureRow>
                  <span>{t('plans.basic.features.remove_watermark')}</span>
                </FeatureRow>
                <FeatureRow>
                  <span>{t('plans.basic.features.live_code_editor')}</span>
                </FeatureRow>
                <FeatureRow>
                  <span>{t('plans.basic.features.support')}</span>
                </FeatureRow>
              </>
            }
          />
        )}

        {/* Premium Plan */}
        {showPremiumCard && (
          <PaidPlanCard
            plan="premium"
            title={t('plans.premium.title')}
            tagline={t('plans.premium.tagline')}
            titleClassName="bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent"
            description={t('plans.premium.description')}
            icon="/icons/premium.svg"
            cardClassName="bg-white/10 border-2 border-purple-500/40 text-white rounded-[20px] shadow-lg shadow-purple-500/20"
            badge={{
              text: t('plans.premium.badge'),
              className:
                'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 text-xs font-semibold',
            }}
            price={getPremiumPrice()}
            isAnnual={isAnnual}
            perMonthText={t('billing.per_month')}
            billedAnnuallyText={(amount) => {
              return t('billing.billed_annually_total', { amount })
            }}
            credits={premiumCredits}
            setCredits={setPremiumCredits}
            creditOptions={premiumCreditOptions}
            domains={premiumDomains}
            setDomains={setPremiumDomains}
            domainOptions={domainOptions}
            showDomainSelect={showDomainSelect}
            getButtonText={getButtonText}
            getSubscriptionAction={getSubscriptionAction}
            isButtonDisabled={isButtonDisabled}
            handleSubscribe={handleSubscribe}
            disabledTooltip={getDisabledTooltip()}
            isCurrentPlan={shouldShowCurrentPlan && currentPlan === 'premium'}
            shouldHighlight={highlightedPlan === 'premium'}
            features={
              <>
                <div>
                  <span>
                    {t('plans.premium.features.everything_basic_plus')}
                  </span>
                </div>
                <FeatureRow>
                  <span>
                    {t('plans.premium.features.custom_domains', {
                      count: premiumDomains,
                    })}
                  </span>
                </FeatureRow>
                <FeatureRow>
                  <FeatureWithTooltip
                    text={t('plans.premium.features.accept_payments')}
                    tooltip={t(
                      'plans.premium.features.accept_payments_tooltip',
                    )}
                  />
                </FeatureRow>
                <FeatureRow>
                  <FeatureWithTooltip
                    text={t('plans.premium.features.ai_seo')}
                    tooltip={t('plans.premium.features.ai_seo_tooltip')}
                  />
                </FeatureRow>
                <FeatureRow>
                  <span className="break-text text-wrap">
                    {t('plans.premium.features.custom_code')}
                  </span>
                </FeatureRow>
                <FeatureRow>
                  <span className="break-text text-wrap">
                    {t('plans.premium.features.workspace_member_management')}
                  </span>
                </FeatureRow>
              </>
            }
          />
        )}

        {/* Platinum Plan */}
        {showPlatinumCard && (
          <PaidPlanCard
            plan="platinum"
            title={t('plans.platinum.title')}
            tagline={t('plans.platinum.tagline')}
            titleClassName="bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200 bg-clip-text text-transparent"
            description={t('plans.platinum.description')}
            icon="/icons/platinum.svg"
            price={getPlatinumPrice()}
            isAnnual={isAnnual}
            perMonthText={t('billing.per_month')}
            billedAnnuallyText={(amount) => {
              return t('billing.billed_annually_total', { amount })
            }}
            credits={platinumCredits}
            setCredits={setPlatinumCredits}
            creditOptions={platinumCreditOptions}
            domains={platinumDomains}
            setDomains={setPlatinumDomains}
            domainOptions={domainOptions}
            getButtonText={getButtonText}
            showDomainSelect={showDomainSelect}
            getSubscriptionAction={getSubscriptionAction}
            isButtonDisabled={isButtonDisabled}
            handleSubscribe={handleSubscribe}
            disabledTooltip={getDisabledTooltip()}
            isCurrentPlan={shouldShowCurrentPlan && currentPlan === 'platinum'}
            shouldHighlight={highlightedPlan === 'platinum'}
            features={
              <>
                <div>
                  <span>
                    {t('plans.platinum.features.everything_premium_plus')}
                  </span>
                </div>
                <FeatureRow>
                  <span>
                    {t('plans.platinum.features.custom_domains', {
                      count: platinumDomains,
                    })}
                  </span>
                </FeatureRow>
                <FeatureRow>
                  <FeatureWithTooltip
                    text={t('plans.platinum.features.task_automation')}
                    tooltip={t(
                      'plans.platinum.features.task_automation_tooltip',
                    )}
                  />
                </FeatureRow>
                <FeatureRow>
                  <FeatureWithTooltip
                    text={t('plans.platinum.features.priority_support')}
                    tooltip={t(
                      'plans.platinum.features.priority_support_tooltip',
                    )}
                  />
                </FeatureRow>
                <FeatureRow>
                  <span>
                    {t('plans.platinum.features.priority_feature_requests')}
                  </span>
                </FeatureRow>
                <FeatureRow>
                  <span>
                    {t('plans.platinum.features.advanced_analytics_reporting')}
                  </span>
                </FeatureRow>
              </>
            }
          />
        )}

        {/* Enterprise/Agent + Human Advisor Plan */}
        {/* {showEnterpriseCard && (
          <Card className="bg-[#2a2a2a] border-none text-white rounded-[20px]">
            <CardHeader>
              <CardTitle className="text-2xl">
                {t('plans.enterprise.title')}
              </CardTitle>
              <CardDescription className="text-white opacity-80 text-base mt-2">
                {t('plans.enterprise.subtitle')}
              </CardDescription>
              <CardDescription className="text-white opacity-60 text-sm px-[] mt-2 font-light">
                {t('plans.enterprise.tagline')}
              </CardDescription>
              <div className="pt-4">
                <span className="text-4xl font-semibold">
                  {t('plans.enterprise.price')}
                </span>
                <span className="ml-1 text-white opacity-60">
                  {t('plans.enterprise.price_suffix')}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                className="w-full"
                variant={'active'}
                onClick={() => pushModal('ContactUsModal')}
              >
                {t('plans.enterprise.button')}
              </Button>
              <div className="space-y-3 text-sm">
                <FeatureRow>
                  <span>{t('plans.enterprise.features.feature_1')}</span>
                </FeatureRow>
                <FeatureRow>
                  <span>{t('plans.enterprise.features.feature_2')}</span>
                </FeatureRow>
                <FeatureRow>
                  <span>{t('plans.enterprise.features.feature_3')}</span>
                </FeatureRow>
                <FeatureRow>
                  <span>{t('plans.enterprise.features.feature_4')}</span>
                </FeatureRow>
              </div>
            </CardContent>
          </Card>
        )} */}
      </div>

      {/* Removed ScrollIndicator - no longer needed with vertical mobile layout */}

      {/* Add Additional Domains Toggle */}
      {setShowDomainToggle && (
        <div className="flex items-center justify-end gap-1 mt-4 text-xs px-4 container mx-auto max-w-7xl">
          <span className="text-white/60">Need</span>
          <button
            onClick={() => {
              if (!(curDomains && curDomains > 1)) {
                setShowDomainToggle(!showDomainToggle)
              }
            }}
            disabled={!!(curDomains && curDomains > 1)}
            className={cn(
              'underline transition-colors font-medium',
              showDomainToggle
                ? 'text-white'
                : 'text-white/60 hover:text-white',
              curDomains && curDomains > 1 && 'opacity-50 cursor-not-allowed',
            )}
          >
            additional domains?
          </button>
        </div>
      )}
    </section>
  )
}
